/**
 * BOSS SYSTEM — BATTERY NINE.
 * Spec: bible 04-BOSSES §2.
 *
 * A captured mobile air-defense battery from Fort Riley. 12,000 HP across four
 * independently destructible components, three phases, and a death sequence
 * that walks through the model rather than simply hiding it.
 *
 * The design contract that makes a boss a boss rather than a big enemy:
 *   - every destructible CHANGES WHAT IT CAN DO, not just how it looks
 *   - every attack declares its gap, and the gap is visible before the shot
 *   - the fight is a conversation: it asks, you answer, it asks differently
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OFFENCE REWRITE — "it should give me a run for my money".
 *
 * The first pass fired one five-fan every 1.8 s from two emitters. That is a
 * turret, not a boss: the player could park, hold fire, and win. Everything
 * below raises density and cadence hard while keeping the gap law intact.
 *
 * The three phases now have distinct, learnable ATTACK IDENTITIES:
 *   SURVEY      — wide STATIC geometry. It never chases; it tells you where to
 *                 stand. Fast enough to be constant work, honest enough to read.
 *   BRACKET     — aimed RAKES (three walking salvos) alternating with statics.
 *                 Statics say "stand here"; the rake says "not any more".
 *   ROOTEXPOSED — desperation. Everything layered: dense fans under a running
 *                 spiral, plus the two heavy attacks — ROOT LANCE and CINCH.
 *
 * FAIRNESS IS STRUCTURAL, NOT ASPIRATIONAL. Four rules are enforced in code
 * rather than trusted to authoring:
 *   1. AIM_WINDUP (0.28 s) on every aimed volley, with a visible charge flare
 *      at the emitter, and the aim point LOCKED when the flare lights. The shot
 *      goes where you were when the tell started, never where you will be.
 *   2. HEAVY_WINDUP (0.95 s) on the lance, 0.85 s on the cinch, both with the
 *      path drawn on screen — the lance even pre-runs its own sweep once.
 *   3. Gap-bearing shapes fire from ONE emitter. Two emitters 4.3 u apart firing
 *      the same gapped fan interleave at range and seal each other's hole; the
 *      old code did exactly that. `Emitter` makes it impossible.
 *   4. Nothing spawns within SPAWN_CLEAR (1.6 u) of the player. Ever.
 *
 * Angular spacing is preserved across difficulty tiers by widening the arc when
 * the count grows, so ACE gets more screen coverage and never a tighter corridor.
 */

import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { applySurfaceTextures } from '../assets/propTextures'
import type { BulletSystem } from './BulletSystem'
import { emit, type PatternSpec } from './PatternGrammar'
import { getDifficulty } from '../game/difficulty'

export type ComponentId = 'b9_dish' | 'b9_drum_l' | 'b9_drum_r' | 'b9_root'

interface Component {
  id: ComponentId
  hp: number
  maxHp: number
  node: THREE.Object3D | null
  alive: boolean
  /** Local offset used as the fire origin for this component. */
  originX: number
}

export const enum BossPhase {
  /** 100-72%. Wide static geometry. Teaches the read. */
  Survey = 0,
  /** 72-38%. Aimed rakes alternating with statics. Two questions per beat. */
  Bracket = 1,
  /** 38-0%. Layered fire plus the two heavy attacks. */
  RootExposed = 2,
}

/**
 * Which hardpoints a volley leaves from.
 *
 * `centre` is mandatory for anything carrying an authored hole — a gap only
 * survives if a single emitter owns the whole shape.
 */
type Emitter = 'centre' | 'drums' | 'alternate'

interface Volley {
  spec: PatternSpec
  emitter: Emitter
  /** Repeats of this volley per trigger — the rake. */
  salvos?: number
  /** Seconds between rake salvos. Each salvo re-locks its own aim point. */
  salvoDelay?: number
}

/* ── Fairness constants. These are the contract; do not tune them away. ───── */

/** Minimum wind-up on any aimed volley. Bible floor is 220 ms. */
const AIM_WINDUP = 0.28
/** Minimum wind-up on a heavy. Bible floor is 650 ms. */
const LANCE_WINDUP = 0.95
const CINCH_WINDUP = 0.85
/** Dead air after a phase threshold so the player can reposition. Floor 1.2 s. */
const PHASE_LOCKOUT = 1.35
/** No projectile may exist this close to the player at spawn. */
const SPAWN_CLEAR = 1.6
/**
 * Reference engagement range for the spacing proof, in world units. A player
 * pressing the boss hard sits about here; anything further only widens the gap.
 */
const REF_RANGE = 9
/**
 * Required centre-to-centre spacing of adjacent projectiles at REF_RANGE.
 * Pellet render radius is 0.22 and the player hitbox is 0.22, so 1.90 leaves
 * 1.46 u of clear air — comfortably over the 1.10 u floor.
 */
const MIN_SPACING = 1.9

/**
 * Attack tables. Every entry's angular step is >= 12.1 deg, which is the
 * spacing floor at REF_RANGE: 9 u * 12.1 deg = 1.90 u between neighbours.
 */
const ATTACKS: Record<BossPhase, Volley[]> = {
  /**
   * SURVEY — static geometry only. Nothing here tracks, so the whole phase is
   * "read the shape, pick the lane, hold it". Cadence roughly doubled from the
   * first pass, and the wall gives a hole wide enough to walk through at trim
   * speed, which is what teaches the player that holes are the subject.
   */
  [BossPhase.Survey]: [
    // 7 over 74 deg = 12.3 deg step. Open fan: escaped between the rays.
    { spec: { kind: 'fan', count: 7, speed: 12, spreadDeg: 74, angleDeg: 270, cadenceHz: 1.05, telegraphMs: 300, bullet: 'pellet' }, emitter: 'centre' },
    // 11 over a 26 u span, 2-wide hole = 4.7 u corridor, persists the whole descent.
    { spec: { kind: 'wall_with_gap', count: 11, speed: 10, spreadDeg: 26, gapIndex: 3, gapWidth: 2, angleDeg: 270, cadenceHz: 0.95, telegraphMs: 340, bullet: 'pellet' }, emitter: 'centre' },
    { spec: { kind: 'fan', count: 7, speed: 12.5, spreadDeg: 74, angleDeg: 270, cadenceHz: 1.05, telegraphMs: 300, bullet: 'pellet' }, emitter: 'centre' },
    { spec: { kind: 'wall_with_gap', count: 11, speed: 10, spreadDeg: 26, gapIndex: 6, gapWidth: 2, angleDeg: 270, cadenceHz: 0.95, telegraphMs: 340, bullet: 'pellet' }, emitter: 'centre' },
  ],

  /**
   * BRACKET — the phase that makes you leave. The rake is three aimed salvos
   * 0.24 s apart, each locking its own aim at its own flare, so it walks across
   * the player's path instead of landing in one place. Statics between rakes
   * keep the field honest and give the rake something to chase you into.
   */
  [BossPhase.Bracket]: [
    { spec: { kind: 'aimed_burst', count: 3, speed: 19, spreadDeg: 9, aimed: true, cadenceHz: 1.15, telegraphMs: 300, bullet: 'bolt' }, emitter: 'drums', salvos: 3, salvoDelay: 0.24 },
    // 9 over 100 deg = 12.5 deg step, one member out at index 4.
    { spec: { kind: 'fan', count: 9, speed: 13, spreadDeg: 100, angleDeg: 270, gapIndex: 4, gapWidth: 1, cadenceHz: 1.0, telegraphMs: 320, bullet: 'pellet' }, emitter: 'centre' },
    { spec: { kind: 'aimed_burst', count: 2, speed: 20, spreadDeg: 7, aimed: true, cadenceHz: 1.25, telegraphMs: 300, bullet: 'bolt' }, emitter: 'drums', salvos: 2, salvoDelay: 0.26 },
    // Ring with a 3-wide aperture. Expands, so spacing only ever grows.
    { spec: { kind: 'ring', count: 18, speed: 11, gapIndex: 7, gapWidth: 3, cadenceHz: 0.9, telegraphMs: 340, bullet: 'pellet' }, emitter: 'centre' },
    { spec: { kind: 'converging_lanes', count: 6, speed: 11, spreadDeg: 20, angleDeg: 270, cadenceHz: 1.05, telegraphMs: 320, bullet: 'blob' }, emitter: 'centre' },
  ],

  /**
   * ROOTEXPOSED — desperation. Denser, faster, and layered: a running spiral
   * under wide fans, with the lance and the cinch punctuating. The centre gap
   * migrates at 4 u/s the whole time so no lane is a permanent answer.
   */
  [BossPhase.RootExposed]: [
    // 11 over 128 deg = 12.8 deg step. Near-horizontal outer rays leave frame.
    { spec: { kind: 'fan', count: 11, speed: 14, spreadDeg: 128, angleDeg: 270, gapIndex: 5, gapWidth: 1, cadenceHz: 1.45, telegraphMs: 300, bullet: 'pellet' }, emitter: 'centre' },
    { spec: { kind: 'aimed_burst', count: 3, speed: 21, spreadDeg: 8, aimed: true, cadenceHz: 1.35, telegraphMs: 300, bullet: 'bolt' }, emitter: 'drums', salvos: 3, salvoDelay: 0.22 },
    // 5 arms, 72 deg apart, walking 26 deg per volley — corridors sweep.
    { spec: { kind: 'spiral', count: 5, speed: 12, rotationPerVolley: 26, cadenceHz: 1.3, telegraphMs: 300, bullet: 'pellet' }, emitter: 'centre' },
    { spec: { kind: 'converging_lanes', count: 8, speed: 12, spreadDeg: 18, angleDeg: 270, cadenceHz: 1.2, telegraphMs: 320, bullet: 'blob' }, emitter: 'centre' },
    { spec: { kind: 'ring', count: 20, speed: 12, gapIndex: 4, gapWidth: 3, cadenceHz: 1.1, telegraphMs: 320, bullet: 'pellet' }, emitter: 'centre' },
    { spec: { kind: 'petal', count: 5, speed: 10, spreadDeg: 18, gapIndex: 2, gapWidth: 1, cadenceHz: 1.0, telegraphMs: 340, bullet: 'pellet' }, emitter: 'centre' },
  ],
}

/**
 * SATURATION FALLBACK. With the dish gone nothing can aim, so the battery
 * stops trying and starts covering ground instead. Losing the tracker is a
 * trade, not a win.
 */
const SATURATE: Volley = {
  spec: { kind: 'spiral', count: 6, speed: 11, rotationPerVolley: 31, cadenceHz: 1.35, telegraphMs: 300, bullet: 'pellet' },
  emitter: 'centre',
}

const TOTAL_HP = 12000

/** One volley waiting on its wind-up. The aim point is locked at queue time. */
interface Pending {
  spec: PatternSpec
  ox: number
  oy: number
  aimX: number
  aimY: number
  /** Seconds of wind-up remaining. */
  wind: number
  windMax: number
  spin: number
}

/** The two heavies. `none` means the ordinary volley scheduler owns the field. */
type Heavy = 'none' | 'lance' | 'cinch'

export class BossSystem {
  readonly group = new THREE.Group()

  private components: Component[] = [
    { id: 'b9_dish', hp: 1800, maxHp: 1800, node: null, alive: true, originX: 0 },
    { id: 'b9_drum_l', hp: 2000, maxHp: 2000, node: null, alive: true, originX: -2.15 },
    { id: 'b9_drum_r', hp: 2000, maxHp: 2000, node: null, alive: true, originX: 2.15 },
    { id: 'b9_root', hp: 6200, maxHp: 6200, node: null, alive: true, originX: 0 },
  ]

  private model: THREE.Object3D | null = null
  active = false
  private dying = false
  private _defeated = false
  /**
   * TRUE ONLY WHEN THE SPECTACLE IS OVER.
   *
   * The game layer treats this as "stage complete" and freezes the simulation
   * the moment it flips — which is precisely why the old death sequence never
   * played: it was set on the killing blow, so `stepDeath()` was never ticked
   * again. Holding it back until the wreck has finished burning is what makes
   * the ending land instead of cutting.
   */
  get defeated(): boolean {
    return this._defeated
  }

  /** True from the killing blow until the wreck settles. Boss is inert. */
  get inDeathThroes(): boolean {
    return this.dying
  }

  private x = 0
  private y = 17
  private age = 0
  private fireTimer = 0
  private phase: BossPhase = BossPhase.Survey
  private attackIndex = 0
  /** Advances per volley so spirals, rings and petals rotate. */
  private spin = 0
  /** Phase 3 centre gap migrates at 4 u/s — tracked so fire follows it. */
  private gapDrift = 0
  /** Death clock in sequence-seconds (slowed near the kill). */
  private deathTimer = 0
  /** Death clock in real seconds. Drives the slow-motion ramp. */
  private deathReal = 0
  /** Index of the next death beat to fire. */
  private deathBeat = 0
  /** (2) Core-reveal animation timer. -1 until the first drum breaks. */
  private revealTimer = -1
  /** (3) Last phase seen, so transitions fire exactly once. */
  private lastPhase: BossPhase | null = null
  /** (3) Seconds into the current phase-transition flourish. */
  private transitionTimer = -1
  /** Seconds of enforced dead air. Set by phase changes and heavy recovery. */
  private lockout = 0
  /** (4) Arena pipework HP. Destructible cover the player fights around. */
  private readonly pipeHp = [900, 900, 900, 900]
  private readonly pipeNames = ['Pipe_L', 'Pipe_R', 'Pipe_Fwd', 'Pipe_Aft']
  /** Callback so the game can spawn VFX/audio for boss theatre. */
  onEvent: ((
    kind: 'reveal' | 'phase' | 'pipe' | 'part' | 'salvo' | 'drones' | 'beam' | 'lance' | 'cinch' | 'death',
    x: number,
    y: number,
  ) => void) | null = null
  /**
   * VFX bridge. Structurally typed and every member optional so it accepts an
   * EffectsSystem whether or not `spawnBossDeath` has landed yet — the build
   * can never break on the other agent's work in progress.
   */
  effects: { spawnBossDeath?: (x: number, y: number) => void } | null = null
  /** Special-attack rotation timer. */
  private specialTimer = 6
  private specialIndex = 0
  /** Heart pulse clock. Drives the 3x vulnerability window. */
  private heartPhase = 0
  /** Extra core burst timer, live only once the heart is exposed. */
  private coreBurstTimer = 5.5
  /** Per-component destruction cinematics, keyed by component id. */
  private cinematics: { id: ComponentId; t: number; x: number; y: number }[] = []
  /** Volleys mid-wind-up. */
  private pending: Pending[] = []
  /** Which drum fires next for `alternate` volleys. */
  private altSide = 0

  /* ── Heavy attack state ─────────────────────────────────────────────────── */
  private heavy: Heavy = 'none'
  private heavyT = 0
  /** Lance sweep bounds, in world radians. */
  private lanceFrom = 0
  private lanceTo = 0
  private lanceEmit = 0
  /** Cinch ring: centre, aperture bearing, and whether it has spawned. */
  private cinchX = 0
  private cinchY = 0
  private cinchAperture = 0
  private cinchFired = false

  /* ── Telegraph layer. Owned outright so no attack can ship untelegraphed. ── */
  private readonly fx = new THREE.Group()
  private readonly flares: THREE.Mesh[] = []
  /** [0] sweep start guide, [1] sweep end guide, [2] live/preview ray. */
  private readonly lanceRays: THREE.Mesh[] = []
  private readonly cinchRing: THREE.Mesh
  private readonly cinchGate: THREE.Mesh
  private readonly fxGeometries: THREE.BufferGeometry[] = []
  private readonly fxMaterials: THREE.MeshBasicMaterial[] = []

  constructor() {
    const quad = new THREE.PlaneGeometry(1, 1)
    this.fxGeometries.push(quad)
    const makeQuad = (color: number, order: number): THREE.Mesh => {
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      })
      this.fxMaterials.push(mat)
      const mesh = new THREE.Mesh(quad, mat)
      mesh.frustumCulled = false
      mesh.renderOrder = order
      mesh.visible = false
      this.fx.add(mesh)
      return mesh
    }
    // Charge flares — one per possible simultaneous emitter.
    for (let i = 0; i < 4; i += 1) this.flares.push(makeQuad(0xffd08a, 8))
    // Lance guides are cold; the live ray is hot. Colour carries the meaning.
    this.lanceRays.push(makeQuad(0x7fd8ec, 8), makeQuad(0x7fd8ec, 8), makeQuad(0xff8a4c, 9))

    const ringGeo = new THREE.RingGeometry(0.975, 1.0, 72)
    const gateGeo = new THREE.RingGeometry(0.92, 1.06, 20, 1, 0, 1)
    this.fxGeometries.push(ringGeo, gateGeo)
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xff8a4c, transparent: true, opacity: 0, blending: THREE.AdditiveBlending,
      depthWrite: false, toneMapped: false, side: THREE.DoubleSide,
    })
    const gateMat = new THREE.MeshBasicMaterial({
      color: 0xdffcff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending,
      depthWrite: false, toneMapped: false, side: THREE.DoubleSide,
    })
    this.fxMaterials.push(ringMat, gateMat)
    this.cinchRing = new THREE.Mesh(ringGeo, ringMat)
    this.cinchGate = new THREE.Mesh(gateGeo, gateMat)
    for (const m of [this.cinchRing, this.cinchGate]) {
      m.frustumCulled = false
      m.renderOrder = 8
      m.visible = false
      this.fx.add(m)
    }
    // Behind the projectile layer (z 1.2) — a telegraph must never hide a shot.
    this.fx.position.z = 1.0
    this.group.add(this.fx)
  }

  async load(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync('/models/thunderhead/boss-battery-nine.glb')
      this.model = gltf.scene
      // BATTERY NINE is the largest object in the stage and shipped entirely
      // in flat colour — the texture pass lived inside PropField and nothing
      // else ever called it. Every hull, deck and revetment surface now gets
      // the same procedural treatment the farm structures do.
      applySurfaceTextures(this.model)
      this.model.rotation.x = Math.PI / 2
      // (1) SCALE. BATTERY NINE must dominate the playfield — the player
      // should fly AROUND it, not orbit a vehicle. At 1.85x the chassis spans
      // ~20 of the 36 unit playfield, so lateral space becomes a real resource.
      this.model.scale.setScalar(1.85)
      // Bind each destructible node by name so damage can hide it precisely.
      for (const c of this.components) {
        c.node = this.model.getObjectByName(c.id) ?? null
      }
      this.group.add(this.model)
      // Do NOT blindly hide here: load() is async and may resolve AFTER
      // spawn() has already made the boss active. Respect current state.
      this.group.visible = this.active
    } catch (err) {
      console.warn('BATTERY NINE model unavailable', err)
    }
  }

  /**
   * Per-component health for the HUD readout. Without this the player is
   * shooting a black box — they cannot tell which part is nearly broken, so
   * the destructible design is invisible to them.
   */
  componentStatus(): { id: ComponentId; frac: number; alive: boolean }[] {
    return this.components.map((c) => ({
      id: c.id,
      frac: Math.max(0, c.hp) / c.maxHp,
      alive: c.alive,
    }))
  }

  /** Total remaining HP as a 0..1 fraction. Drives phase transitions. */
  get healthFraction(): number {
    const remaining = this.components.reduce((s, c) => s + Math.max(0, c.hp), 0)
    return remaining / TOTAL_HP
  }

  /**
   * THE WEAK POINT.
   *
   * Once a drum is gone the deck plating hinges back and the Lattice heart is
   * exposed. It pulses BRIGHT for 0.9 s of every 2.4 s, and hits landed inside
   * that window do 3x damage.
   */
  get heartVulnerable(): boolean {
    return this.coreExposed && this.heartPhase < 0.9
  }

  /** 0..1 brightness for the heart, so the tell is visible before it matters. */
  get heartGlow(): number {
    if (!this.coreExposed) return 0
    const t = this.heartPhase
    if (t < 0.25) return t / 0.25
    if (t < 0.9) return 1
    return Math.max(0, 1 - (t - 0.9) / 0.3)
  }

  /** The core only becomes vulnerable once a drum is gone (bible §2). */
  private get coreExposed(): boolean {
    return !this.components[1].alive || !this.components[2].alive
  }

  /** 0, 1 or 2. Every point of this makes the survivor angrier, not quieter. */
  private get drumsLost(): number {
    return (this.components[1].alive ? 0 : 1) + (this.components[2].alive ? 0 : 1)
  }

  /**
   * Death-sequence time dilation, 0..1. Exposed so the game layer can drive a
   * global slow-motion off it if it wants to; the wreck animation already uses
   * it internally, so the kill reads as a stagger even without wiring.
   */
  get deathTimeScale(): number {
    if (!this.dying) return 1
    if (this.deathReal < 0.14) return 0
    const t = Math.min(1, (this.deathReal - 0.14) / 1.5)
    return 0.18 + 0.82 * (t * t * (3 - 2 * t))
  }

  spawn(): void {
    this.active = true
    this.dying = false
    this._defeated = false
    this.age = 0
    this.y = 17
    this.group.visible = true
  }

  update(dt: number, playerX: number, playerY: number, bullets: BulletSystem): void {
    if (!this.active) return
    this.age += dt

    if (this.dying) {
      this.hideTelegraphs()
      // The bullet field belongs to a boss that no longer exists. Leaving live
      // rounds up through a death cinematic is the cheapest death in the genre.
      if (this.deathReal < 1.6) bullets.clear()
      this.stepDeath(dt)
      return
    }

    // Entry: a slow, heavy descent. Four seconds of a machine this size
    // lowering into frame is worth more than any amount of screen shake.
    if (this.age < 4) {
      const t = this.age / 4
      const eased = 1 - Math.pow(1 - t, 3)
      this.y = 20 - eased * 8
    } else {
      // Strafes a narrow band — at this scale it cannot swing far without
      // leaving the playfield, and the constraint makes it feel massive.
      this.x = Math.sin((this.age - 4) * 0.26) * 4.2
    }
    this.group.position.set(this.x, this.y, 0)

    // Phase from remaining health.
    const f = this.healthFraction
    this.phase = f > 0.72 ? BossPhase.Survey : f > 0.38 ? BossPhase.Bracket : BossPhase.RootExposed

    // (3) PHASE TRANSITIONS AS THEATRE. Each threshold visibly changes the
    // machine rather than silently swapping an attack table — and buys the
    // player PHASE_LOCKOUT seconds of clean air to reposition into it.
    if (this.lastPhase !== null && this.phase !== this.lastPhase) {
      this.transitionTimer = 0
      this.lockout = PHASE_LOCKOUT
      this.pending.length = 0
      this.heavy = 'none'
      this.specialTimer = Math.max(this.specialTimer, PHASE_LOCKOUT + 0.8)
      this.onEvent?.('phase', this.x, this.y)
    }
    this.lastPhase = this.phase
    if (this.transitionTimer >= 0) {
      this.transitionTimer += dt
      const t = Math.min(1, this.transitionTimer / 1.4)
      // The whole frame shudders and settles — a machine changing its mind.
      if (this.model) {
        this.model.rotation.z = Math.sin(t * Math.PI * 3) * 0.05 * (1 - t)
        this.model.position.y = -Math.sin(t * Math.PI) * 0.5
      }
      if (t >= 1) this.transitionTimer = -1
    }

    this.stepModelTheatre(dt)

    if (this.phase === BossPhase.RootExposed) {
      this.gapDrift += dt * 4 // 4 u/s migration, per spec
    }

    this.updateOffence(dt, playerX, playerY, bullets)
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * OFFENCE
   * ───────────────────────────────────────────────────────────────────────*/

  private updateOffence(dt: number, playerX: number, playerY: number, bullets: BulletSystem): void {
    if (this.lockout > 0) this.lockout -= dt

    // Wind-ups always resolve, even through a lockout: they were telegraphed
    // before the lockout began, so cancelling them would be the surprise.
    for (const p of this.pending) {
      p.wind -= dt
      if (p.wind > 0) continue
      this.release(p, bullets, playerX, playerY)
    }
    this.pending = this.pending.filter((p) => p.wind > 0)
    this.drawFlares()

    if (this.age <= 3) return

    // Heavies own the field while they run.
    if (this.heavy !== 'none') {
      this.stepHeavy(dt, playerX, playerY, bullets)
      // ROOTEXPOSED layers a slow wide fan under the lance. Five rays over
      // 120 deg is a 30 deg step — 4.7 u of clear air at REF_RANGE.
      if (this.heavy === 'lance' && this.phase === BossPhase.RootExposed) {
        this.fireTimer -= dt
        if (this.fireTimer <= 0) {
          this.fireTimer = 1.1 * getDifficulty().cadenceScale
          this.queue(
            { kind: 'fan', count: 5, speed: 9, spreadDeg: 120, angleDeg: 270, cadenceHz: 1, telegraphMs: 320, bullet: 'pellet' },
            'centre', 0, playerX, playerY,
          )
        }
      }
      return
    }

    if (this.lockout > 0) return

    this.fireTimer -= dt
    if (this.fireTimer <= 0) this.fire(playerX, playerY)

    // CORE BURST. Once the heart is showing it vents on its own clock, so the
    // reward for exposing it is also a new thing to survive.
    if (this.coreExposed) {
      this.coreBurstTimer -= dt
      if (this.coreBurstTimer <= 0) {
        this.coreBurstTimer = this.phase === BossPhase.RootExposed ? 4.2 : 5.8
        this.queue(
          { kind: 'ring', count: 16, speed: 9.5, gapIndex: (this.attackIndex * 5) % 16, gapWidth: 3, cadenceHz: 1, telegraphMs: 360, bullet: 'blob' },
          'centre', 0, playerX, playerY,
        )
      }
    }

    // SPECIALS. Punctuation rather than one continuous stream, and the table
    // changes per phase so escalation is structural.
    this.specialTimer -= dt
    if (this.specialTimer <= 0) this.startSpecial(playerX, playerY)
  }

  /** Schedule the next ordinary volley (or rake) from the phase table. */
  private fire(playerX: number, playerY: number): void {
    const table = ATTACKS[this.phase]
    // Dish gone: the tracker is dead, so the battery saturates instead of aims.
    const dishAlive = this.components[0].alive
    const entry = !dishAlive && this.attackIndex % 2 === 1
      ? SATURATE
      : table[this.attackIndex % table.length]
    this.attackIndex += 1
    this.spin += 1

    // ENRAGE. Each lost drum tightens cadence by ~20% and thickens the shape.
    // Losing a launcher must make the survivor read as cornered and furious.
    const rage = Math.pow(0.8, this.drumsLost)
    this.fireTimer = (1 / entry.spec.cadenceHz) * getDifficulty().cadenceScale * rage

    const salvos = (entry.salvos ?? 1) + (this.drumsLost > 0 && entry.emitter === 'drums' ? 1 : 0)
    const delay = entry.salvoDelay ?? 0.26
    for (let s = 0; s < salvos; s += 1) {
      this.queue(entry.spec, entry.emitter, s * delay, playerX, playerY)
    }
  }

  /**
   * Put a volley on the wire with its wind-up, locking the aim point NOW.
   *
   * Locking at queue time rather than at release is the whole fairness story
   * for aimed fire: the flare lights, and wherever the player was at that
   * instant is where the round is going. Standing still is punished; reacting
   * always works.
   */
  private queue(spec: PatternSpec, emitter: Emitter, extra: number, playerX: number, playerY: number): void {
    // Runaway guard — a stalled frame must never dump ten volleys at once.
    if (this.pending.length > 12) return
    const scaled = this.scale(spec)
    const wind = Math.max(spec.aimed ? AIM_WINDUP : spec.telegraphMs / 1000, 0.24) + extra
    const oy = this.y - 1.2
    const drift = this.phase === BossPhase.RootExposed ? Math.sin(this.gapDrift * 0.25) * 5 : 0

    const origins: number[] = []
    if (emitter === 'drums') {
      if (this.components[1].alive) origins.push(this.components[1].originX)
      if (this.components[2].alive) origins.push(this.components[2].originX)
      if (origins.length === 0) origins.push(0)
    } else if (emitter === 'alternate') {
      const live = this.components.filter((c, i) => (i === 1 || i === 2) && c.alive)
      origins.push(live.length ? live[this.altSide++ % live.length].originX : 0)
    } else {
      // Gap-bearing geometry: exactly one emitter, so the hole cannot be sealed.
      origins.push(0)
    }

    for (const ox of origins) {
      this.pending.push({
        spec: scaled,
        ox: this.x + ox + drift,
        oy,
        aimX: playerX,
        aimY: playerY,
        wind,
        windMax: wind,
        spin: this.spin * 1.3,
      })
    }
  }

  /**
   * Difficulty scaling that cannot pinch a corridor.
   *
   * Raising `count` alone narrows the angular step, which is exactly the thing
   * the fairness rules forbid. So the arc widens with the count: ACE gets more
   * of the screen covered at the SAME spacing, never a tighter hole.
   */
  private scale(spec: PatternSpec): PatternSpec {
    const diff = getDifficulty()
    // Enrage thickens shapes too — a cornered battery throws more, not less.
    const rageCount = 1 + 0.22 * this.drumsLost
    const count = Math.max(1, Math.round(spec.count * diff.bulletScale * rageCount))
    const out: PatternSpec = { ...spec, count, speed: spec.speed * diff.speedScale }

    if (count !== spec.count) {
      if (spec.kind === 'fan' || spec.kind === 'petal') {
        const from = Math.max(1, spec.count - 1)
        const to = Math.max(1, count - 1)
        out.spreadDeg = Math.min(168, (spec.spreadDeg ?? 60) * (to / from))
      } else if (spec.kind === 'wall_with_gap') {
        // spreadDeg is a world-unit span here; grow it so the pitch holds.
        out.spreadDeg = (spec.spreadDeg ?? 26) * (count / spec.count)
      }
    }
    // A wider count means a wider hole too, so the gap scales with the shape.
    if (spec.gapWidth !== undefined && count > spec.count) {
      out.gapWidth = Math.max(spec.gapWidth, Math.round(spec.gapWidth * (count / spec.count)))
    }
    // Verify the shape still clears MIN_SPACING at REF_RANGE; widen if not.
    if ((out.kind === 'fan' || out.kind === 'petal') && out.count > 1) {
      const step = ((out.spreadDeg ?? 60) * Math.PI) / 180 / (out.count - 1)
      if (step * REF_RANGE < MIN_SPACING) {
        out.spreadDeg = (((MIN_SPACING / REF_RANGE) * (out.count - 1)) * 180) / Math.PI
      }
    }
    return out
  }

  /** Wind-up elapsed: expand the volley and put it on the field. */
  private release(p: Pending, bullets: BulletSystem, playerX: number, playerY: number): void {
    const shots = emit(p.spec, p.ox, p.oy, p.aimX, p.aimY, p.spin)
    const life = p.spec.speed > 18 ? 5 : 9
    for (const b of shots) {
      this.push(bullets, b.x, b.y, b.vx, b.vy, b.radius * 0.55, life, playerX, playerY)
    }
  }

  /**
   * The last line of the fairness contract: nothing materialises on top of the
   * player. A round that would spawn inside SPAWN_CLEAR is simply not fired.
   */
  private push(
    bullets: BulletSystem,
    x: number, y: number, vx: number, vy: number,
    radius: number, life: number,
    playerX: number, playerY: number,
  ): void {
    const dx = x - playerX
    const dy = y - playerY
    if (dx * dx + dy * dy < SPAWN_CLEAR * SPAWN_CLEAR) return
    bullets.spawn(x, y, vx, vy, radius, life)
  }

  /* ── Specials & heavies ─────────────────────────────────────────────────── */

  private startSpecial(playerX: number, playerY: number): void {
    const f = this.healthFraction
    this.specialTimer = f > 0.6 ? 7.0 : f > 0.35 ? 5.4 : 4.2

    // Per-phase special rotation. SURVEY never sees a heavy — it is the phase
    // that teaches reading, and a lance on top of that is noise, not pressure.
    const table: readonly ('salvo' | 'drones' | 'lance' | 'cinch')[] =
      this.phase === BossPhase.Survey ? ['salvo', 'drones']
        : this.phase === BossPhase.Bracket ? ['lance', 'salvo', 'lance', 'drones']
          : ['lance', 'cinch', 'salvo', 'lance', 'cinch', 'drones']

    const kind = table[this.specialIndex % table.length]
    this.specialIndex += 1

    if (kind === 'lance') {
      this.beginLance(playerX)
    } else if (kind === 'cinch') {
      this.beginCinch(playerX, playerY)
    } else {
      // Missiles and escorts stay on the existing game-layer channel.
      this.onEvent?.(kind, this.x, this.y)
    }
  }

  /**
   * ROOT LANCE — the new attack, and the one the player has to learn.
   *
   * A rotary beam that sweeps ONE HALF of the field: from 8 deg off vertical
   * out to 104 deg, always on the side the player is currently standing on. It
   * cannot be outrun — a pivot sweep's ground trace moves faster than cruise
   * speed at range, and pretending otherwise is how sweeping beams become
   * cheap. So it is not survived by reflex; it is survived by READING and
   * RELOCATING, which is what the 0.95 s telegraph buys.
   *
   * The telegraph is three things, in order:
   *   1. two cold guide rays marking the exact first and last angle of the arc,
   *      up at t=0.05 — the swept wedge is legible immediately;
   *   2. a preview ray that runs the whole sweep once between 0.15 s and 0.72 s,
   *      so the DIRECTION is shown, not inferred;
   *   3. the guides pulsing hot for the last 0.23 s as the commit tell.
   *
   * Escape budget: the worst case is the far edge of the threatened half to
   * two units past the boss centreline — about 14.6 u, or 0.70 s at cruise
   * speed. The telegraph gives 0.95 s. The answer is always reachable.
   */
  private beginLance(playerX: number): void {
    const side = playerX >= this.x ? 1 : -1
    const down = -Math.PI / 2
    this.lanceFrom = down + side * 0.14
    this.lanceTo = down + side * 1.815
    this.heavy = 'lance'
    this.heavyT = 0
    this.lanceEmit = 0
    this.onEvent?.('lance', this.x, this.y)
  }

  /**
   * CINCH — a converging ring with exactly one aperture, dropped around the
   * player. The ring closes at 8.5 u/s from 13.5 u out, so the aperture arc
   * shrinks proportionally and still measures 1.29 u of clear air at a radius
   * of 1.5 u. It never closes below the floor before it reaches the middle.
   *
   * The aperture is always aimed DOWN-FIELD AND INWARD, so the escape lane can
   * never point into a playfield wall.
   */
  private beginCinch(playerX: number, playerY: number): void {
    // Clamp the centre so the whole ring stays inside the cull bounds and the
    // player is always genuinely inside it rather than pinned on the rim.
    this.cinchX = Math.max(-10, Math.min(10, playerX))
    this.cinchY = Math.max(-13, Math.min(2, playerY))
    const tx = this.cinchX > 0 ? this.cinchX - 8 : this.cinchX + 8
    const ty = this.cinchY - 9
    this.cinchAperture = Math.atan2(ty - this.cinchY, tx - this.cinchX)
    this.cinchFired = false
    this.heavy = 'cinch'
    this.heavyT = 0
    this.onEvent?.('cinch', this.cinchX, this.cinchY)
  }

  private stepHeavy(dt: number, playerX: number, playerY: number, bullets: BulletSystem): void {
    this.heavyT += dt
    if (this.heavy === 'lance') this.stepLance(dt, playerX, playerY, bullets)
    else if (this.heavy === 'cinch') this.stepCinch(playerX, playerY, bullets)
  }

  private stepLance(dt: number, playerX: number, playerY: number, bullets: BulletSystem): void {
    const t = this.heavyT
    const SWEEP = 1.35
    const END = LANCE_WINDUP + SWEEP + 0.45

    if (t < LANCE_WINDUP) {
      // Guides up almost immediately; the wedge is readable from the first frame.
      const guide = Math.min(1, t / 0.12) * (t > LANCE_WINDUP - 0.23 ? 0.55 + 0.45 * Math.abs(Math.sin(t * 34)) : 0.42)
      this.ray(0, this.lanceFrom, 34, 0.18, guide)
      this.ray(1, this.lanceTo, 34, 0.18, guide)
      // Preview pass — the sweep shows its own direction before it commits.
      if (t > 0.15 && t < 0.72) {
        const k = (t - 0.15) / 0.57
        this.ray(2, this.lanceFrom + (this.lanceTo - this.lanceFrom) * k, 34, 0.1, 0.5)
      } else {
        this.hideRay(2)
      }
      return
    }

    if (t < LANCE_WINDUP + SWEEP) {
      const k = (t - LANCE_WINDUP) / SWEEP
      const angle = this.lanceFrom + (this.lanceTo - this.lanceFrom) * k
      this.ray(0, this.lanceFrom, 34, 0.1, 0.22)
      this.ray(1, this.lanceTo, 34, 0.1, 0.22)
      this.ray(2, angle, 34, 0.62 + Math.sin(t * 40) * 0.08, 0.95)
      // Emit at a fixed 22 Hz rather than per frame: the beam stays a beam at
      // any refresh rate, and the pool never floods.
      this.lanceEmit -= dt
      if (this.lanceEmit <= 0) {
        this.lanceEmit = 1 / 22
        const ox = this.x
        const oy = this.y - 1.2
        for (let i = 0; i < 9; i += 1) {
          const d = 2.2 + i * 2.6
          this.push(
            bullets,
            ox + Math.cos(angle) * d, oy + Math.sin(angle) * d,
            Math.cos(angle) * 24, Math.sin(angle) * 24,
            0.25, 1.0, playerX, playerY,
          )
        }
      }
      return
    }

    // Recovery — guides fade, and the battery holds fire for a beat.
    const fade = 1 - (t - LANCE_WINDUP - SWEEP) / 0.45
    for (let i = 0; i < 3; i += 1) this.rayFade(i, Math.max(0, fade) * 0.3)
    if (t >= END) {
      this.hideTelegraphs()
      this.heavy = 'none'
      this.lockout = 0.35
      this.fireTimer = 0.25
    }
  }

  private stepCinch(playerX: number, playerY: number, bullets: BulletSystem): void {
    const t = this.heavyT
    const R = 13.5
    if (t < CINCH_WINDUP) {
      const a = Math.min(1, t / 0.15) * (0.3 + 0.25 * Math.abs(Math.sin(t * 12)))
      this.cinchRing.visible = true
      this.cinchRing.position.set(this.cinchX - this.x, this.cinchY - this.y, 0)
      this.cinchRing.scale.setScalar(R * (1 + 0.02 * Math.sin(t * 9)))
      ;(this.cinchRing.material as THREE.MeshBasicMaterial).opacity = a
      // The aperture is the loudest thing on screen — the answer, not the threat.
      const halfAp = (2.6 / 22) * Math.PI
      this.cinchGate.visible = true
      this.cinchGate.position.copy(this.cinchRing.position)
      this.cinchGate.scale.setScalar(R)
      this.cinchGate.rotation.z = this.cinchAperture - halfAp
      ;(this.cinchGate.material as THREE.MeshBasicMaterial).opacity = 0.55 + 0.45 * Math.abs(Math.sin(t * 7))
      return
    }

    if (!this.cinchFired) {
      this.cinchFired = true
      const n = 22
      const halfAp = (2.6 / n) * Math.PI
      for (let i = 0; i < n; i += 1) {
        const a = (i / n) * Math.PI * 2
        const d = Math.abs(((a - this.cinchAperture + Math.PI * 3) % (Math.PI * 2)) - Math.PI)
        if (d < halfAp) continue
        const speed = 8.5 * getDifficulty().speedScale
        this.push(
          bullets,
          this.cinchX + Math.cos(a) * R, this.cinchY + Math.sin(a) * R,
          -Math.cos(a) * speed, -Math.sin(a) * speed,
          0.24, 3.2, playerX, playerY,
        )
      }
    }

    // Hold the aperture marker through the close so the lane stays legible for
    // its entire useful life, then hand the field back.
    const k = (t - CINCH_WINDUP) / 1.9
    ;(this.cinchRing.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.32 * (1 - k))
    ;(this.cinchGate.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.8 * (1 - k))
    this.cinchRing.scale.setScalar(R * Math.max(0.05, 1 - k))
    this.cinchGate.scale.setScalar(R * Math.max(0.05, 1 - k))
    if (k >= 1) {
      this.cinchRing.visible = false
      this.cinchGate.visible = false
      this.heavy = 'none'
      this.lockout = 0.3
      this.fireTimer = 0.2
    }
  }

  /* ── Telegraph drawing ──────────────────────────────────────────────────── */

  /** Charge flares scale and brighten across the wind-up of each pending volley. */
  private drawFlares(): void {
    for (let i = 0; i < this.flares.length; i += 1) {
      const p = this.pending[i]
      const mesh = this.flares[i]
      if (!p) {
        mesh.visible = false
        continue
      }
      const k = 1 - Math.max(0, p.wind) / Math.max(0.001, p.windMax)
      mesh.visible = true
      mesh.position.set(p.ox - this.x, p.oy - this.y, 0)
      const s = 0.55 + k * 1.5
      mesh.scale.set(s, s, 1)
      ;(mesh.material as THREE.MeshBasicMaterial).opacity = 0.25 + k * 0.6
    }
  }

  /** Place one lance ray as a quad running from the emitter along `angle`. */
  private ray(index: number, angle: number, len: number, thick: number, alpha: number): void {
    const mesh = this.lanceRays[index]
    mesh.visible = alpha > 0.01
    mesh.position.set(Math.cos(angle) * len * 0.5, Math.sin(angle) * len * 0.5 - 1.2, 0)
    mesh.rotation.z = angle
    mesh.scale.set(len, thick, 1)
    ;(mesh.material as THREE.MeshBasicMaterial).opacity = alpha
  }

  private rayFade(index: number, alpha: number): void {
    const mesh = this.lanceRays[index]
    ;(mesh.material as THREE.MeshBasicMaterial).opacity = alpha
    mesh.visible = alpha > 0.01
  }

  private hideRay(index: number): void {
    this.lanceRays[index].visible = false
  }

  private hideTelegraphs(): void {
    for (const m of this.lanceRays) m.visible = false
    for (const m of this.flares) m.visible = false
    this.cinchRing.visible = false
    this.cinchGate.visible = false
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * MODEL THEATRE — reveal, cinematics, heart, vents.
   * ───────────────────────────────────────────────────────────────────────*/

  private stepModelTheatre(dt: number): void {
    // (2) THE EYEBALL MOMENT. Breaking a drum peels the deck armour back and
    // lights the core. It is a staged reveal, not a boolean.
    if (this.coreExposed && this.revealTimer < 0) {
      this.revealTimer = 0
      this.onEvent?.('reveal', this.x, this.y)
    }
    const glow = this.model?.getObjectByName('CoreGlow')
    if (this.revealTimer >= 0) {
      this.revealTimer += dt
      const t = Math.min(1, this.revealTimer / 1.2)
      for (let i = 0; i < 6; i++) {
        const plate = this.model?.getObjectByName(`DeckPlate_${i}`)
        if (!plate) continue
        const dir = i < 3 ? -1 : 1
        plate.position.z = 0.1 + dir * t * 1.5
        plate.rotation.x = dir * t * 0.9
      }
      if (glow) {
        glow.visible = true
        const pulse = t < 1 ? 1 + (1 - t) * 2.5 : 1 + Math.sin(this.age * 5) * 0.25
        glow.scale.setScalar(pulse)
      }
    } else if (glow) {
      glow.visible = false
    }

    // Dish sweep lamp pulses during the track window; when the dish is dead all
    // surviving fire goes static, so the lamp goes dark and stays dark.
    const lamp = this.model?.getObjectByName('DishSweepLamp')
    if (lamp) lamp.visible = this.components[0].alive && Math.sin(this.age * 6) > 0

    // CINEMATIC COMPONENT DEATH. Each destroyed part lurches, sheds, sinks and
    // goes dark across 1.4 s instead of vanishing.
    for (const c of this.cinematics) {
      const node = this.components.find((k) => k.id === c.id)?.node
      if (!node) continue
      const t = Math.min(1, c.t / 1.4)
      if (t < 0.25) {
        node.rotation.z = Math.sin(c.t * 46) * 0.16 * (1 - t / 0.25)
      } else {
        const k = (t - 0.25) / 0.75
        node.rotation.z = 0.3 + k * 0.7
        node.position.y = -k * 1.6
        node.scale.setScalar(Math.max(0.001, 1 - k * 0.9))
      }
      if (t >= 1) node.visible = false
    }

    // Drums counter-rotate while alive, and spin up as the fight degrades.
    const rageSpin = 1 + this.drumsLost * 0.9
    for (const [i, name] of [[1, 'b9_drum_l_Cluster'], [2, 'b9_drum_r_Cluster']] as const) {
      const node = this.model?.getObjectByName(name)
      if (node && this.components[i].alive) node.rotation.y += dt * (i === 1 ? 1.1 : -1.1) * rageSpin
    }

    // Heart pulse. 2.4 s cycle, independent of everything else so the player
    // can learn it as a beat.
    this.heartPhase = (this.heartPhase + dt) % 2.4
    const heart = this.model?.getObjectByName('b9_heart')
    if (heart) {
      heart.visible = this.coreExposed
      if (this.coreExposed) {
        const g = this.heartGlow
        heart.scale.setScalar(0.9 + g * 0.45)
        heart.rotation.y += dt * (0.6 + g * 2.4)
      }
    }
    // Emitter irises brighten while a lance is charging or the heart is hot.
    const charge = this.heavy === 'lance' ? Math.min(1, this.heavyT / LANCE_WINDUP) : 0
    for (const n of ['b9_lens_l_Iris', 'b9_lens_r_Iris']) {
      const iris = this.model?.getObjectByName(n)
      if (iris) iris.scale.setScalar(0.8 + Math.max(this.heartGlow, charge) * 1.2)
    }
    // Vents run hotter as the machine degrades.
    const heat = 1 - this.healthFraction
    for (let i = 0; i < 6; i += 1) {
      const v = this.model?.getObjectByName(`b9_vent_${i}_Heat`)
      if (v) v.scale.setScalar(0.5 + heat * 1.6)
    }

    for (const c of this.cinematics) c.t += dt
    this.cinematics = this.cinematics.filter((c) => c.t < 1.4)
  }

  /**
   * (4) THE ARENA PARTICIPATES. The grey field pipework BATTERY NINE is seated
   * in is destructible cover. Breaking a run opens a lane the player can use to
   * cross the arena — so the environment is a resource, not a backdrop.
   */
  damageArena(worldX: number, worldY: number, amount: number): boolean {
    if (!this.active || this.dying || !this.model) return false
    const lx = (worldX - this.x) / 1.85
    const ly = (worldY - this.y) / 1.85
    let idx = -1
    if (lx < -3.6) idx = 0
    else if (lx > 3.6) idx = 1
    else if (ly > 2.2) idx = 2
    else if (ly < -2.2) idx = 3
    if (idx < 0 || this.pipeHp[idx] <= 0) return false

    this.pipeHp[idx] -= amount
    if (this.pipeHp[idx] <= 0) {
      const node = this.model.getObjectByName(this.pipeNames[idx])
      if (node) node.visible = false
      this.onEvent?.('pipe', worldX, worldY)
      return true
    }
    return false
  }

  /**
   * Damage a component. Returns the effective damage applied so the caller can
   * score it and spawn the right feedback.
   */
  damage(worldX: number, worldY: number, amount: number): { hit: boolean; destroyed: ComponentId | null } {
    if (!this.active || this.dying || this._defeated) return { hit: false, destroyed: null }

    const lx = worldX - this.x
    const ly = worldY - this.y
    let target: Component | undefined
    if (lx < -1.1 && this.components[1].alive) target = this.components[1]
    else if (lx > 1.1 && this.components[2].alive) target = this.components[2]
    else if (ly > -0.4 && this.components[0].alive) target = this.components[0]
    else if (this.coreExposed) target = this.components[3]
    else target = this.components.find((c) => c.alive && c.id !== 'b9_root')

    if (!target || !target.alive) return { hit: false, destroyed: null }

    const onHeart = target.id === 'b9_root' && this.coreExposed
    const multiplier = onHeart && this.heartVulnerable ? 3 : 1
    target.hp -= amount * multiplier

    if (target.hp <= 0) {
      target.alive = false
      this.cinematics.push({ id: target.id, t: 0, x: worldX, y: worldY })
      this.onEvent?.('part', worldX, worldY)
      // Losing a launcher makes the survivor angrier immediately: cadence and
      // shape scaling both key off drumsLost, and the next volley comes early.
      if (target.id === 'b9_drum_l' || target.id === 'b9_drum_r') {
        this.fireTimer = Math.min(this.fireTimer, 0.5)
      }
      if (this.components.every((c) => !c.alive) || this.components[3].hp <= 0) {
        this.beginDeath()
      }
      return { hit: true, destroyed: target.id }
    }
    return { hit: true, destroyed: null }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * DEATH
   * ───────────────────────────────────────────────────────────────────────*/

  /**
   * (5) DEATH AS SPECTACLE.
   *
   * The player's note was that the kill "abruptly ended". It did, and for a
   * mechanical reason: the old code set `defeated` on the killing blow, the
   * game layer read that as stage-complete and froze the simulation on the very
   * next frame, so `stepDeath()` was never ticked. Four beats of animation
   * existed and none of them were ever seen.
   *
   * Now the killing blow starts a 7.4 s owned sequence — hitstop, a slow-motion
   * ramp back to real time, secondary detonations all the way through, a core
   * breach, and a long hold on the burning wreck — and only then does
   * `defeated` flip. The arena stays locked and the boss track keeps playing
   * for the whole thing, so the stage cannot resume underneath it.
   */
  private beginDeath(): void {
    this.dying = true
    this.deathTimer = 0
    this.deathReal = 0
    this.deathBeat = 0
    this.heavy = 'none'
    this.pending.length = 0
    this.hideTelegraphs()
    // The kill moment: freeze, shake, and hand the VFX layer the big one.
    this.onEvent?.('phase', this.x, this.y)
    this.bossDeathVfx(this.x, this.y)
  }

  /**
   * Hand off to the VFX layer's boss-death set piece.
   *
   * `spawnBossDeath` is landing concurrently, so it is called through an
   * optional structural type AND mirrored onto the event channel. Whichever
   * arrives first works; neither can break the build.
   */
  private bossDeathVfx(x: number, y: number): void {
    const fx = this.effects
    if (fx && typeof fx.spawnBossDeath === 'function') fx.spawnBossDeath(x, y)
    this.onEvent?.('death', x, y)
  }

  /**
   * Death beats, in sequence-clock seconds. `kind` routes to the game layer's
   * existing channels: 'part' is a heavy secondary, 'phase' is a shockwave with
   * hitstop, 'pipe' is an anchor letting go.
   */
  private static readonly DEATH_BEATS: readonly { t: number; kind: 'part' | 'phase' | 'pipe'; dx: number; dy: number }[] = [
    { t: 0.10, kind: 'part', dx: -4.0, dy: 0.4 },
    { t: 0.55, kind: 'part', dx: 4.0, dy: -0.3 },
    { t: 1.00, kind: 'part', dx: 0.0, dy: 2.0 },
    { t: 1.45, kind: 'pipe', dx: -7.5, dy: 0.0 },
    { t: 1.80, kind: 'pipe', dx: 7.5, dy: 0.0 },
    { t: 2.15, kind: 'pipe', dx: 0.0, dy: 4.4 },
    { t: 2.45, kind: 'pipe', dx: 0.0, dy: -4.4 },
    { t: 2.80, kind: 'part', dx: -2.2, dy: 1.6 },
    { t: 3.40, kind: 'part', dx: 2.6, dy: 1.2 },
    { t: 4.05, kind: 'phase', dx: 0.0, dy: 0.0 },
    { t: 4.35, kind: 'part', dx: -3.2, dy: -1.4 },
    { t: 4.85, kind: 'part', dx: 3.4, dy: -1.0 },
    { t: 5.40, kind: 'part', dx: -1.4, dy: -2.2 },
    { t: 5.95, kind: 'part', dx: 2.0, dy: -1.8 },
    { t: 6.50, kind: 'part', dx: -2.8, dy: 0.6 },
    { t: 7.05, kind: 'part', dx: 1.2, dy: 1.0 },
  ]

  private static readonly DEATH_LENGTH = 7.4

  private stepDeath(dt: number): void {
    this.deathReal += dt
    // SLOW-MOTION. The first fifth of a second is a hard freeze, then time
    // eases back to normal across 1.5 s. The kill lands as a stagger rather
    // than a cut, and it costs the game layer nothing to get it.
    this.deathTimer += dt * this.deathTimeScale
    const t = this.deathTimer

    // Secondary detonations keep going for the entire sequence — the wreck is
    // never quiet, which is the difference between "it died" and "it was
    // destroyed".
    while (
      this.deathBeat < BossSystem.DEATH_BEATS.length &&
      t >= BossSystem.DEATH_BEATS[this.deathBeat].t
    ) {
      const b = BossSystem.DEATH_BEATS[this.deathBeat]
      this.deathBeat += 1
      if (b.kind === 'pipe') {
        const i = Math.min(3, this.deathBeat - 4)
        const node = this.model?.getObjectByName(this.pipeNames[Math.max(0, i)])
        if (node) node.visible = false
      }
      this.onEvent?.(b.kind, this.x + b.dx, this.y + b.dy)
      // The core breach gets its own smoke column.
      if (b.kind === 'phase') this.bossDeathVfx(this.x, this.y)
    }

    if (!this.model) {
      if (t > BossSystem.DEATH_LENGTH) this.finishDeath()
      return
    }

    // Beat 1 (0.0-1.3) — the machine loses its footing. Violent shudder.
    if (t < 1.3) {
      const k = 1 - t / 1.3
      this.model.rotation.z = Math.sin(t * 34) * 0.075 * k
      this.model.position.x = Math.sin(t * 41) * 0.18 * k
    } else if (t < 2.6) {
      // Beat 2 (1.3-2.6) — the pipework anchors let go and it begins to list.
      this.model.rotation.z = (t - 1.3) * 0.2
    }

    // Beat 3 (2.6-3.9) — the dish folds down into the exposed core.
    const dish = this.components[0].node
    if (dish && t >= 2.6) {
      dish.visible = true
      const k = Math.min(1, (t - 2.6) / 1.3)
      dish.rotation.x = k * (Math.PI / 2)
      dish.position.y = 1.35 - k * 1.5
      dish.position.z = -k * 0.4
    }

    // Beat 4 (3.9-5.1) — CORE BREACH. The frame swells, then splits and lists.
    if (t >= 3.9 && t < 5.1) {
      const k = (t - 3.9) / 1.2
      const swell = k < 0.18 ? 1 + (0.18 - k) * 0.9 : 1
      this.model.scale.setScalar(1.85 * swell)
      this.model.rotation.z = 0.26 + k * 0.34
      this.model.position.x = Math.sin(t * 26) * 0.12 * (1 - k)
    }

    // Beat 5 (5.1-7.4) — THE HOLD. It sinks slowly, listing, and keeps burning.
    // Nothing about the stage resumes while this runs: the boss is still
    // `active`, so the scroll stays locked and the boss track keeps playing.
    if (t >= 5.1) {
      const k = Math.min(1, (t - 5.1) / 2.3)
      const eased = k * k * (3 - 2 * k)
      this.model.rotation.z = 0.6 + eased * 0.3
      this.group.position.y = this.y - eased * 2.4
      this.model.scale.setScalar(1.85 * (1 - eased * 0.06))
    }

    if (t > BossSystem.DEATH_LENGTH) this.finishDeath()
  }

  private finishDeath(): void {
    this.dying = false
    this._defeated = true
    this.active = false
    // The hulk STAYS ON SCREEN. The stage-complete state freezes the frame, and
    // a burning wreck under that card reads as an ending; an empty sky reads as
    // a bug.
    this.group.visible = true
  }

  /**
   * Ember anchors for the game layer to burst once the sequence resolves. The
   * heavy work now happens inside stepDeath's beat table; these are the tail.
   */
  deathAnchors(): { x: number; y: number; delay: number }[] {
    const S = 1.85
    return [
      { x: this.x, y: this.y, delay: 0.0 },
      { x: this.x - 1.6 * S, y: this.y - 0.8 * S, delay: 0.18 },
      { x: this.x + 1.6 * S, y: this.y + 0.6 * S, delay: 0.36 },
    ]
  }

  reset(): void {
    this.active = false
    this.dying = false
    this._defeated = false
    this.age = 0
    this.attackIndex = 0
    this.spin = 0
    this.gapDrift = 0
    this.deathTimer = 0
    this.deathReal = 0
    this.deathBeat = 0
    this.revealTimer = -1
    this.transitionTimer = -1
    this.lastPhase = null
    this.lockout = 0
    this.pending.length = 0
    this.heavy = 'none'
    this.heavyT = 0
    this.altSide = 0
    this.specialTimer = 6
    this.specialIndex = 0
    this.coreBurstTimer = 5.5
    this.fireTimer = 0
    this.pipeHp.fill(900)
    this.heartPhase = 0
    this.cinematics = []
    this.group.visible = false
    this.group.position.set(0, 17, 0)
    this.hideTelegraphs()
    if (this.model) {
      this.model.rotation.set(Math.PI / 2, 0, 0)
      this.model.position.set(0, 0, 0)
      this.model.scale.setScalar(1.85)
      for (const n of this.pipeNames) {
        const node = this.model.getObjectByName(n)
        if (node) node.visible = true
      }
      for (const c of this.components) {
        if (!c.node) continue
        c.node.visible = true
        c.node.rotation.set(0, 0, 0)
        c.node.position.y = 0
        c.node.scale.setScalar(1)
      }
      for (let i = 0; i < 6; i++) {
        const plate = this.model.getObjectByName(`DeckPlate_${i}`)
        if (plate) { plate.position.z = 0.1; plate.rotation.x = 0 }
      }
    }
    for (const c of this.components) {
      c.hp = c.maxHp
      c.alive = true
      if (c.node) c.node.visible = true
    }
  }

  dispose(): void {
    this.model?.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.isMesh) {
        m.geometry.dispose()
        for (const mat of Array.isArray(m.material) ? m.material : [m.material]) mat.dispose()
      }
    })
    for (const g of this.fxGeometries) g.dispose()
    for (const m of this.fxMaterials) m.dispose()
  }
}
